import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp } from 'lucide-react';
import SEO from '@/components/SEO';
import { Reveal } from '@/lib/useReveal';
import { RESULT_CASES } from '@/lib/content';

const COLOR = {
  blue: '#0072EE', teal: '#14B8A6', amber: '#F59E0B', coral: '#FB6A48',
};
const SOFT = {
  blue: '#E6F2FE', teal: '#E8FBF8', amber: '#FEF6E6', coral: '#FFF1EC',
};

const EXTRA = [
  { n: '05', client: 'American Pools', category: 'Home Services · Regional', headline: 'Brand refresh + paid acquisition for a 30-year regional brand.', metric: '4.3×', metricLabel: 'Return on Ad Spend', services: ['Brand', 'PPC', 'Social'], color: 'teal' },
  { n: '06', client: 'Confidential B2B SaaS', category: 'Enterprise SaaS', headline: 'AI-search visibility and demand gen for a mid-market platform.', metric: '#1', metricLabel: 'ChatGPT Citation (Category)', services: ['AEO', 'SEO', 'Content'], color: 'blue' },
];
const ALL_CASES = [...RESULT_CASES, ...EXTRA];

export default function WorkPage() {
  return (
    <main data-testid="work-page" className="bg-white">
      <SEO
        title="Our Work - Client Case Studies & Outcomes"
        description="A selection of BrandPartners client work - measurable, transparent outcomes across home services, healthcare, real estate, and B2B."
        path="/work"
      />

      <section className="relative pt-14 lg:pt-20 pb-16 bg-radial-blue overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-40 pointer-events-none"/>
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 relative grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Reveal as="div" className="kicker"><span className="dot"/>The work, the numbers</Reveal>
            <Reveal as="h1" delay={80} className="h-display mt-5 text-[48px] sm:text-[72px] lg:text-[88px]">
              Results <span className="grad-text">we love to brag about.</span>
            </Reveal>
          </div>
          <Reveal as="p" delay={140} className="lg:col-span-4 text-[17px] text-bp-slate leading-relaxed">
            Different industries. Different scales. Same playbook - measurable outcomes, ruthless transparency,
            relationships that last.
          </Reveal>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-6">
          {ALL_CASES.map((r, i) => (
            <Reveal as="article" key={r.n} delay={i * 70} className="card-floating p-7 lg:p-8 group cursor-pointer">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-bp-slate-soft">{r.category}</div>
                  <h2 className="font-display font-bold text-[26px] text-bp-ink mt-2 leading-tight">{r.client}</h2>
                </div>
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center group-hover:rotate-12 transition-transform" style={{background:SOFT[r.color], color:COLOR[r.color]}}>
                  <TrendingUp size={22}/>
                </div>
              </div>
              <p className="mt-4 text-[15.5px] text-bp-slate leading-relaxed">{r.headline}</p>
              <div className="mt-7 grid grid-cols-5 gap-4 items-end">
                <div className="col-span-2">
                  <div className="font-display font-extrabold text-[48px] leading-none tracking-tight" style={{color: COLOR[r.color]}}>{r.metric}</div>
                  <div className="text-[12px] font-semibold uppercase tracking-wider text-bp-slate-soft mt-2">{r.metricLabel}</div>
                </div>
                <div className="col-span-3 flex flex-wrap justify-end gap-2">
                  {r.services.map((s) => (
                    <span key={s} className="px-2.5 py-1 rounded-full bg-bp-slate-100 text-[12px] font-semibold text-bp-slate">{s}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-20 bg-bp-slate-50">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 text-center">
          <h2 className="h-display text-[40px] sm:text-[54px]">Your story <span className="grad-text">next?</span></h2>
          <p className="mt-5 text-[17px] text-bp-slate max-w-xl mx-auto">
            Free audit. Real conversation. No pressure. Let&rsquo;s see what we&rsquo;d do to grow your business.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn-primary" data-testid="work-cta">Book a Strategy Call <ArrowRight size={16}/></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
